import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import noop from 'lodash/noop';

import { CreateFeatureFlagRequest, FeatureFlag, UpdateFeatureFlagRequest } from '../../types/featureFlags.ts';

export interface FeatureFlagsState {
  featureFlags: FeatureFlag[];
  isLoading: boolean;
  error: string;
  isCreating: boolean;
  createSuccess: boolean;
  createError: string;
  isUpdating: boolean;
  updateSuccess: boolean;
  updateError: string;
  isDeleting: boolean;
  deleteSuccess: boolean;
  deleteError: string;
  userFeatureFlags: string[];
  isLoadingUserFeatureFlags: boolean;
  userFeatureFlagsError: string;
}

const initialState: FeatureFlagsState = {
  featureFlags: [],
  isLoading: false,
  error: '',
  isCreating: false,
  createSuccess: false,
  createError: '',
  isUpdating: false,
  updateSuccess: false,
  updateError: '',
  isDeleting: false,
  deleteSuccess: false,
  deleteError: '',
  userFeatureFlags: [],
  isLoadingUserFeatureFlags: true,
  userFeatureFlagsError: '',
};

export const featureFlagsReducer = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    loadFeatureFlags: (state: FeatureFlagsState) => {
      state.isLoading = true;
      state.error = '';
    },
    setFeatureFlags: (state: FeatureFlagsState, action: PayloadAction<FeatureFlag[]>) => {
      state.featureFlags = action.payload;
      state.isLoading = false;
      state.error = '';
    },
    setFeatureFlagsError: (state: FeatureFlagsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createFeatureFlag: (_state: FeatureFlagsState, _action: PayloadAction<CreateFeatureFlagRequest>) => noop(),
    setCreateFeatureFlagLoading: (state: FeatureFlagsState) => {
      state.isCreating = true;
      state.createSuccess = false;
      state.createError = '';
    },
    setCreateFeatureFlagSuccess: (state: FeatureFlagsState, action: PayloadAction<FeatureFlag>) => {
      state.isCreating = false;
      state.createSuccess = true;
      state.createError = '';
      // Add the new feature flag to the list
      state.featureFlags.push(action.payload);
    },
    setCreateFeatureFlagError: (state: FeatureFlagsState, action: PayloadAction<string>) => {
      state.isCreating = false;
      state.createSuccess = false;
      state.createError = action.payload;
    },
    updateFeatureFlag: (
      _state: FeatureFlagsState,
      _action: PayloadAction<{ id: number; request: UpdateFeatureFlagRequest }>,
    ) => noop(),
    setUpdateFeatureFlagLoading: (state: FeatureFlagsState) => {
      state.isUpdating = true;
      state.updateSuccess = false;
      state.updateError = '';
    },
    setUpdateFeatureFlagSuccess: (state: FeatureFlagsState, action: PayloadAction<FeatureFlag>) => {
      state.isUpdating = false;
      state.updateSuccess = true;
      state.updateError = '';
      // Update the feature flag in the list
      const index = state.featureFlags.findIndex((flag) => flag.id === action.payload.id);
      if (index !== -1) {
        state.featureFlags[index] = action.payload;
      }
    },
    setUpdateFeatureFlagError: (state: FeatureFlagsState, action: PayloadAction<string>) => {
      state.isUpdating = false;
      state.updateSuccess = false;
      state.updateError = action.payload;
    },
    deleteFeatureFlag: (_state: FeatureFlagsState, _action: PayloadAction<number>) => noop(),
    setDeleteFeatureFlagLoading: (state: FeatureFlagsState) => {
      state.isDeleting = true;
      state.deleteSuccess = false;
      state.deleteError = '';
    },
    setDeleteFeatureFlagSuccess: (state: FeatureFlagsState, action: PayloadAction<number>) => {
      state.isDeleting = false;
      state.deleteSuccess = true;
      state.deleteError = '';
      // Remove the deleted feature flag from the list
      state.featureFlags = state.featureFlags.filter((flag) => flag.id !== action.payload);
    },
    setDeleteFeatureFlagError: (state: FeatureFlagsState, action: PayloadAction<string>) => {
      state.isDeleting = false;
      state.deleteSuccess = false;
      state.deleteError = action.payload;
    },
    loadUserFeatureFlags: (state: FeatureFlagsState) => {
      state.isLoadingUserFeatureFlags = true;
      state.userFeatureFlagsError = '';
    },
    setUserFeatureFlags: (state: FeatureFlagsState, action: PayloadAction<string[]>) => {
      state.userFeatureFlags = action.payload;
      state.isLoadingUserFeatureFlags = false;
      state.userFeatureFlagsError = '';
    },
    setUserFeatureFlagsError: (state: FeatureFlagsState, action: PayloadAction<string>) => {
      state.isLoadingUserFeatureFlags = false;
      state.userFeatureFlagsError = action.payload;
    },
  },
});
